/**
 * Деньги. Копейки целым числом — в продукте нет ни одного места, где сумма
 * хранится или считается числом с плавающей точкой.
 *
 * Причина простая: 0.1 + 0.2 в double даёт 0.30000000000000004, и на тысяче
 * начислений врачу это превращается в расхождение, которое невозможно объяснить
 * ни владельцу клиники, ни бухгалтеру. Ошибка в деньгах — одна из трёх
 * категорий дефектов, которые в этом проекте считаются недопустимыми.
 */

export type Kopecks = number & { readonly __brand: 'Kopecks' };

export function kopecks(value: number): Kopecks {
  if (!Number.isInteger(value)) {
    throw new Error(`Сумма в копейках обязана быть целой, получено ${String(value)}`);
  }
  return value as Kopecks;
}

export function fromRubles(rubles: number): Kopecks {
  return kopecks(Math.round(rubles * 100));
}

export function toRubles(value: Kopecks): number {
  return value / 100;
}

export function add(...values: readonly Kopecks[]): Kopecks {
  return kopecks(values.reduce<number>((sum, v) => sum + v, 0));
}

export function subtract(from: Kopecks, amount: Kopecks): Kopecks {
  return kopecks(from - amount);
}

/**
 * Процент от суммы: доля врача, скидка, налог.
 * Округление — «половина вверх», как в бухгалтерской практике; банковское
 * округление дало бы расхождение с 1С, где сходится отчётность клиники.
 */
export function percentOf(value: Kopecks, percent: number): Kopecks {
  if (percent < 0 || percent > 100) {
    throw new Error(`Процент вне диапазона: ${String(percent)}`);
  }
  return kopecks(Math.round((value * percent) / 100));
}

/**
 * Разбиение суммы на части (рассрочка) без потери копеек: остаток деления
 * добавляется к первым платежам, поэтому сумма частей всегда равна исходной.
 */
export function split(value: Kopecks, parts: number): Kopecks[] {
  if (!Number.isInteger(parts) || parts < 1) {
    throw new Error(`Число частей должно быть целым и положительным: ${String(parts)}`);
  }
  const base = Math.floor(value / parts);
  const remainder = value - base * parts;
  return Array.from({ length: parts }, (_, i) => kopecks(base + (i < remainder ? 1 : 0)));
}

/** Формат для интерфейса и печати. Валюта одна — рубль. */
export function format(value: Kopecks): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
  }).format(toRubles(value));
}

export { inWords, numberInWords, plural } from './in-words.js';
